/**
 * Shared geometry for the bottom nav (TabBar + HeroDexButton). Centralised here
 * so the coin diameter, the bar height, and the hero's lift offset stay in
 * lockstep across both components instead of being scattered magic numbers.
 *
 * E35 applies two 20% reductions relative to the previous build:
 *   - the centre Dex coin shrinks 120px -> 96px (COIN_DIAMETER_PX), and
 *   - the white menu bar shrinks ~68px -> 54px of content height
 *     (BAR_CONTENT_HEIGHT_PX),
 * while the four nav items stay vertically centred against the shorter bar and
 * the coin keeps its lifted, z-above relationship with the bar.
 */

/** Previous coin diameter, kept only to document the E35 -20% reduction. */
const COIN_DIAMETER_PREV_PX = 120;

/** Centre Dex medallion rendered diameter (E35: 80% of the previous 120px). */
export const COIN_DIAMETER_PX = Math.round(COIN_DIAMETER_PREV_PX * 0.8); // 96

/**
 * Top offset applied to the hero medallion column in TabBar so the central
 * compass disc rises above the bar. The flanking columns must apply the same
 * paddingTop to stay vertically centred against the hero's inflated column
 * height. Changing this value must be matched in both consumers
 * (HeroDexButton marginTop and TabBar flanking-column paddingTop).
 */
export const HERO_TOP_OFFSET_PX = 28;

/**
 * Visible content height of the white menu bar (the box the four nav items are
 * vertically centred within), before safe-area padding. E35 sets this to 80% of
 * the previous ~68px so the bar is 20% shorter; items centre inside it via
 * flex so icon + label never clip.
 */
const BAR_CONTENT_HEIGHT_PREV_PX = 68;
export const BAR_CONTENT_HEIGHT_PX = Math.round(BAR_CONTENT_HEIGHT_PREV_PX * 0.8); // 54

/**
 * Hero coin wrapper height. Tracks the coin diameter so the medallion column is
 * exactly as tall as the coin it holds (was a hardcoded 56px against a 120px
 * coin; kept proportional at ~47% of the diameter).
 */
export const HERO_WRAPPER_HEIGHT_PX = Math.round(COIN_DIAMETER_PX * 0.58); // 56

/**
 * E42: horizontal clearance added on top of the coin diameter when reserving the
 * centre placeholder slot. The placeholder must be at least as wide as the coin
 * or the inner labels (FRIENDS / TRADE) slide under the medallion; E37's 90%
 * value (86px) was NARROWER than the 96px coin, which is exactly why those two
 * labels were still partly hidden behind the coin at 390pt. This clearance is
 * split evenly to both sides of the coin so adjacent labels never touch it.
 */
export const CENTER_CLEARANCE_PX = 16;

/**
 * E42: width of the hidden centre placeholder slot (slot 3), DERIVED from the
 * coin diameter plus clearance rather than a magic constant. Because it is now
 * always >= COIN_DIAMETER_PX, the four real items (Profile, Friends, Trade,
 * Calendar, each flex-1 basis-0) are pushed outward far enough that FRIENDS and
 * TRADE clear the coin at any width. Changing COIN_DIAMETER_PX moves this in
 * lockstep, so the gap can never fall behind the coin again.
 */
export const CENTER_PLACEHOLDER_WIDTH_PX = COIN_DIAMETER_PX + CENTER_CLEARANCE_PX; // 112

/**
 * E37: fraction of the home-indicator safe-area inset that each nav item keeps
 * as its bottom padding. E35 used the FULL inset on every item, which (stacked
 * on the nav's own 0.5x inset) floated the icon + label too high off the bottom
 * edge. Keeping only this fraction of the inset on the item pulls the buttons
 * closer to the bottom while still leaving clearance over the home indicator;
 * the freed-up space also raises the available room above the content, so the
 * centred icon + label rise as the brief asks. Lower = buttons sit lower.
 */
export const ITEM_BOTTOM_INSET_FRACTION = 0.5;

/**
 * E48: the E44 upward lift is now ZERO. E44 added a fixed upward padding
 * (a quarter of the coin diameter, ~24px) on the nav so the label row rose
 * toward the coin's vertical midline; that same padding held the bar floor
 * (and the coin anchored bottom:0 to it) UP off the physical bottom edge, so
 * the round coin's bottom never reached the screen bottom. The user now wants
 * the coin's bottom edge AT the physical bottom, so we dial the E44 lift back
 * to 0: with no upward padding the bar floor sits at the bottom edge and the
 * coin (anchored bottom:0, overflowing downward) reaches the screen bottom.
 *
 * It stays a single DERIVED expression so the contract is explicit and
 * test-asserted (lift = COIN_DIAMETER_PX * 0, i.e. exactly 0 for any coin
 * size): the whole assembly drops by the former lift amount while the
 * coin-to-items relationship (HERO_TOP_OFFSET_PX) and the horizontal centre-gap
 * constants (CENTER_PLACEHOLDER_WIDTH_PX / CENTER_CLEARANCE_PX) are unchanged.
 * The bar's own height (BAR_CONTENT_HEIGHT_PX) is unchanged too; only the
 * vertical OFFSET from the bottom edge changes. The labels keep a minimal
 * safe-area floor via ITEM_BOTTOM_INSET_FRACTION on each item so they stay
 * clear of the home indicator while the coin overhangs past it.
 */
export const BAR_BOTTOM_LIFT_PX = COIN_DIAMETER_PX * 0; // 0 (E48: E44 lift removed)

/**
 * E52: extra height added to the TOP of the white nav panel so its top edge
 * rises up to the hero coin's vertical midline (the "yellow line" the user drew
 * in IMG_0859). The buttons, labels, and coin do NOT move; only the white panel
 * grows upward. Its bottom stays pinned at the physical screen bottom
 * (BAR_BOTTOM_LIFT_PX stays 0).
 *
 * Geometry (y measured upward from the bar floor / screen bottom):
 *   - The hero coin column is lifted by HERO_TOP_OFFSET_PX (28) and the coin is
 *     COIN_DIAMETER_PX (96) tall, so the coin's vertical midline sits at
 *     COIN_MIDLINE_PX = HERO_TOP_OFFSET_PX + COIN_DIAMETER_PX / 2
 *                     = 28 + 48 = 76px above the bar floor.
 *   - The current panel top edge is at BAR_CONTENT_HEIGHT_PX (54px) above the
 *     bar floor (the item row height; bottom lift is 0).
 *   - To land the panel top on the coin midline the panel must grow upward by
 *     the difference: BAR_TOP_RISE_PX = COIN_MIDLINE_PX - BAR_CONTENT_HEIGHT_PX
 *                                     = 76 - 54 = 22px.
 *
 * It is a single DERIVED constant (a documented function of the coin geometry,
 * not a bare literal) so it is the one knob to nudge if the on-device midline
 * needs a small visual tweak against the yellow-line reference. Applied as
 * paddingTop on the <nav> so the white background extends up by this amount
 * while the item row keeps its existing bottom placement (the items remain a
 * fixed-height row pinned to the bottom band; only the background top rises).
 */
export const COIN_MIDLINE_PX = HERO_TOP_OFFSET_PX + COIN_DIAMETER_PX / 2; // 76
export const BAR_TOP_RISE_PX = COIN_MIDLINE_PX - BAR_CONTENT_HEIGHT_PX; // 22

/**
 * E54 fix 5: the rightmost nav label "CALENDAR" (the longest single-word label,
 * 8 uppercase chars) was clipped at 390pt and below. Each of the four nav items
 * is a flex-1 basis-0 column sharing the row width LEFT OVER after the fixed
 * centre placeholder (CENTER_PLACEHOLDER_WIDTH_PX). The label was rendered with
 * Tailwind `tracking-widest` (0.1em); that wide letter-spacing on the 8-char
 * word overflowed the narrow column on small screens.
 *
 * The fix caps the label letter-spacing so the longest label fits the narrowest
 * supported column. Budget, computed (px, worst case = iPhone SE 320pt, no side
 * safe-area inset):
 *   - row width available to the four items = 320 - CENTER_PLACEHOLDER_WIDTH_PX
 *       = 320 - 112 = 208px, shared four ways => 52px per item column,
 *   - minus the px-1 horizontal padding (4px each side = 8px) => ~44px of text
 *       space per label (TAB_ITEM_TEXT_BUDGET_PX below),
 *   - "CALENDAR" at 9px (TAB_LABEL_FONT_PX) with an average glyph advance of
 *       ~0.58em is ~41.8px of glyphs BEFORE letter-spacing,
 *   - the 8 letters add 7 inter-letter gaps of `tracking` em each; to keep the
 *       word within the ~44px budget the per-gap spacing must be small.
 *
 * So we cap the label tracking at TAB_LABEL_TRACKING_EM (0.02em, roughly
 * Tailwind `tracking-wide`/`tracking-normal` territory) instead of the old
 * 0.1em `tracking-widest`. This keeps the longest label inside the narrowest
 * column while staying legible, and is fluid (it is a relative em cap, not a
 * 390pt pixel hardcode). It is the single knob to nudge if a future longer label
 * needs more room.
 */
export const TAB_LABEL_FONT_PX = 9;
export const TAB_LABEL_LONGEST = "CALENDAR";
export const TAB_ITEM_TEXT_BUDGET_PX =
  (320 - CENTER_PLACEHOLDER_WIDTH_PX) / 4 - 8; // ~44px on iPhone SE 320pt
export const TAB_LABEL_TRACKING_EM = 0.02;
/**
 * Conservative upper bound on the rendered width of the longest label at the
 * capped tracking: glyph advance (~0.58em avg) for every char plus the
 * inter-letter gaps (chars - 1) at the tracking em, all in px. Used by the
 * guard test to assert the longest label fits the narrowest column budget.
 */
export const TAB_LABEL_LONGEST_WIDTH_PX =
  TAB_LABEL_LONGEST.length * TAB_LABEL_FONT_PX * 0.58 +
  (TAB_LABEL_LONGEST.length - 1) * TAB_LABEL_FONT_PX * TAB_LABEL_TRACKING_EM;
